import {
  getDisabledEnquireAndWithdrawFlag,
  getSubSpecialtyFormFilterFlag,
} from '@/app/utils/getServerFeatureFlag';
import styles from '@/pages/jobs/style.module.scss';
import { getJobSpecialities } from '@/services/cmsService';
import {
  extractPerfectlyMatchingResults,
  extractSimilarJobs,
  parseQueryForGetJobs,
} from '@/utils/jobListingHelper';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { GetServerSidePropsResult } from 'next/types';
import React from 'react';
import { getJobs } from 'services/jobService';
import { sentence, title } from 'to-case';
import { IJobDetail, IJobSpeciality, JobSearchCriteria, JobSearchWebCriteria } from 'types/jobs';
import { textlengthLimit } from 'utils/limitTextLength';
import {
  specialCharSpecialities,
  specialCharSpecialitiesAndSubSpecialties,
} from 'utils/specialities-mapping';

import JobListing from 'modules/JobListing';
import { ContainerStyle, UserLoggedinContainer } from 'modules/UserLoggedinContainer';

export interface JobsListProps {
  jobs: {
    total: number;
    data: IJobDetail[];
    limit: number;
  };
  similarJobs: IJobDetail[];
  jobSpecialities: IJobSpeciality[];
  query: JobSearchWebCriteria;
  metadata: {
    title: string;
    description: string;
  };
  showSubSpecialtyFormFilter: boolean;
  disableEnquireAndWithdrawButtons: boolean;
}

const JobsList = ({
  jobs,
  similarJobs,
  query,
  jobSpecialities,
  metadata,
  showSubSpecialtyFormFilter,
  disableEnquireAndWithdrawButtons,
}: JobsListProps) => {
  return (
    <UserLoggedinContainer
      size={'lg'}
      className={styles.jobListContainer}
      style={ContainerStyle.TINY_BLUE_BANNER}
    >
      <NextSeo
        title={metadata.title}
        description={metadata.description}
        openGraph={{
          title: metadata.title,
          description: metadata.description,
          type: 'website',
        }}
      />
      <JobListing
        jobs={jobs}
        similarJobs={similarJobs}
        query={query}
        jobSpecialities={jobSpecialities}
        showSubSpecialtyFormFilter={showSubSpecialtyFormFilter}
        disableEnquireAndWithdrawButtons={disableEnquireAndWithdrawButtons}
      />
    </UserLoggedinContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<JobsListProps>> {
  const { query } = ctx;
  const { listing, ...queryinfo } = query;

  const showSubSpecialtyFormFilter = await getSubSpecialtyFormFilterFlag(ctx);
  const filteredKeys: {
    grade?: string;
    speciality?: string;
    rootSpeciality?: string;
    subspeciality?: string;
  } = getGradeSpeciality(listing as string[], showSubSpecialtyFormFilter);

  if (filteredKeys.grade && filteredKeys.grade === 'smo-moss') {
    queryinfo.grade = 'SMO/MOSS'; // Hard coded
  } else if (filteredKeys.grade) {
    queryinfo.grade = title(filteredKeys.grade);
  }

  if (filteredKeys.speciality) {
    queryinfo.speciality =
      specialCharSpecialities[filteredKeys.speciality] ?? sentence(filteredKeys.speciality);
  }

  if (filteredKeys.rootSpeciality) {
    // API still expects speciality
    queryinfo.rootSpeciality =
      specialCharSpecialitiesAndSubSpecialties[filteredKeys.rootSpeciality] ??
      title(filteredKeys.rootSpeciality);
  }
  if (filteredKeys.subspeciality) {
    queryinfo.subspeciality =
      specialCharSpecialitiesAndSubSpecialties[filteredKeys.subspeciality] ??
      title(filteredKeys.subspeciality);
  }

  const convertedQuery = parseQueryForGetJobs(queryinfo);

  const [jobs, jobSpecialities, disableEnquireAndWithdrawButtons] = await Promise.all([
    getJobs(convertedQuery, ctx),
    getJobSpecialities(),
    getDisabledEnquireAndWithdrawFlag(ctx),
  ]);

  const { metaTitle, description }: { metaTitle: string; description: string } = getMetaData(
    filteredKeys,
    convertedQuery,
  );

  return {
    props: {
      jobs: extractPerfectlyMatchingResults(jobs),
      similarJobs: extractSimilarJobs(jobs.data),
      jobSpecialities: jobSpecialities ?? [],
      query: queryinfo,
      metadata: { title: metaTitle, description },
      showSubSpecialtyFormFilter,
      disableEnquireAndWithdrawButtons,
    },
  };
}

export default JobsList;

const getMetaData = (
  keys: { grade?: string; speciality?: string },
  convertedQuery: JobSearchCriteria,
) => {
  let metaTitle: string, description: string;
  if (keys.speciality) {
    metaTitle = `Search ${convertedQuery.grade} ${convertedQuery.speciality} jobs`;
    description = `Find your ideal ${convertedQuery.grade} ${convertedQuery.speciality} job with Medrecruit. New jobs added daily. Free end to end service. Make sure your next job's the right one. `;
  } else {
    metaTitle = `Search ${convertedQuery.grade} jobs`;
    description = `Find your next ${convertedQuery.grade} job with Medrecruit.  We'll make sure your next job suits your career, finance & lifestyle goals. Free service for ${convertedQuery.grade}'s.`;
  }
  if (metaTitle?.length > 60) {
    metaTitle = textlengthLimit(metaTitle, 60, true);
  }
  return { metaTitle, description };
};

const getGradeSpeciality = (listing: Array<string>, showSubSpecialityFormFeatureFlag: boolean) => {
  return listing.reduce(
    (
      accumulator: {
        grade?: string;
        speciality?: string;
        rootSpeciality?: string;
        subspeciality?: string;
      },
      currentValue,
      index,
    ) => {
      if (!currentValue) {
        return accumulator;
      }
      if (index === 0) {
        accumulator.grade = currentValue;
      } else if (index === 1 && !showSubSpecialityFormFeatureFlag) {
        accumulator.speciality = currentValue;
      } else if (index === 1 && showSubSpecialityFormFeatureFlag) {
        accumulator.rootSpeciality = currentValue;
      } else if (index === 2 && showSubSpecialityFormFeatureFlag) {
        accumulator.subspeciality = currentValue;
      }
      return accumulator;
    },
    {},
  );
};
